import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import FloatingHeader from '../components/layout/FloatingHeader';
import PageMeta from '../components/PageMeta';
import Footer from '../components/Footer';
import Lichtszene from '../components/licht/Lichtszene';
import { SEITEN_BLENDE } from '../components/animations/seitenBlende';
import '../components/recht/recht.css';
import './VertragWiderrufen.css';

// Elektronische Widerrufsfunktion nach § 356a BGB (Pflicht seit 19.06.2026, Fund H8).
// Einstieg ist der Knopf „Vertrag widerrufen“ im Footer (auf jeder Seite, ohne Login — gekauft wird
// auch ohne Konto). Hier: Schritt 1 die drei erlaubten Angaben (Name, welcher Vertrag, E-Mail für
// die Eingangsbestätigung — KEIN Grund, keine Pflicht-Bestellnummer), Schritt 2 der Knopf
// „Widerruf bestätigen“; erst der schickt ab. Danach die Bestätigung mit Datum und Uhrzeit des
// Eingangs; dieselben Angaben kommen per E-Mail (backend/server.py, POST /api/withdrawal).
// Keine Zwischenschritte, kein „Bist du sicher?“, keine Angebote — das verbietet das Gesetz sinngemäß.

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Gleiche Bezeichnungen wie WITHDRAWAL_TYPES in server.py
const TYPEN = {
  kauf: ['Kauf eines Werkzeugs (Einmalkauf)', 'Purchase of a tool (one-time purchase)'],
  abo: ['All-in-One-Abo', 'All-in-One subscription'],
};

const zusammen = s => s.split(/\s+/).filter(Boolean).join(' ');

// Wortlaut der Erklärung — muss zu _widerruf_erklaerung() in server.py passen, denn genau dieser
// Text steht später in der Eingangsbestätigung.
function erklaerung({ name, typ, angaben, datum }, en) {
  const teile = [];
  if (TYPEN[typ]) teile.push(TYPEN[typ][en ? 1 : 0]);
  const a = zusammen(angaben).replace(/[ .;]+$/, '');
  if (a) teile.push(a);
  if (datum) {
    const [j, m, t] = datum.split('-');
    const d = en || !t ? datum : `${t}.${m}.${j}`;
    teile.push(en ? `ordered on ${d}` : `bestellt am ${d}`);
  }
  const vertrag = teile.join('; ');
  return en
    ? `I, ${zusammen(name)}, hereby withdraw from the contract concluded by me for: ${vertrag}.`
    : `Hiermit widerrufe ich, ${zusammen(name)}, den von mir abgeschlossenen Vertrag über: ${vertrag}.`;
}

const MAIL_OK = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function pruefe(w, en) {
  const f = {};
  if (!zusammen(w.name)) f.name = en ? 'Please enter your name.' : 'Bitte gib deinen Namen an.';
  if (!MAIL_OK.test(w.email.trim())) f.email = en ? 'Please enter a valid email address so we can send you the confirmation.' : 'Bitte gib eine gültige E-Mail-Adresse an, damit wir dir die Bestätigung schicken können.';
  if (!w.typ && !w.angaben.replace(/[\s.;]/g, '')) f.vertrag = en
    ? 'Please tell us which contract you want to withdraw from: choose one above or describe it here.'
    : 'Bitte gib an, welchen Vertrag du widerrufen möchtest: oben auswählen oder hier beschreiben.';
  return f;
}

const heute = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export default function VertragWiderrufen() {
  const location = useLocation();
  const en = location.pathname === '/en' || location.pathname.startsWith('/en/');
  const T = (de, e) => (en ? e : de);
  const p = en ? '/en' : '';
  const belehrung = en ? '/en/withdrawal' : '/widerruf';

  const [werte, setWerte] = useState({ name: '', email: '', typ: '', angaben: '', datum: '', website: '' });
  const [fehler, setFehler] = useState({});
  const [versucht, setVersucht] = useState(false);
  const [sendet, setSendet] = useState(false);
  const [meldung, setMeldung] = useState(null); // 'server' | 'zuviel' | { text }
  const [ergebnis, setErgebnis] = useState(null);
  const karte = useRef(null);
  const meldungRef = useRef(null);

  const setze = (feld, wert) => {
    const neu = { ...werte, [feld]: wert };
    setWerte(neu);
    if (versucht) setFehler(pruefe(neu, en));
  };

  const vollstaendig = Object.keys(pruefe(werte, en)).length === 0;
  const text = useMemo(() => erklaerung(werte, en), [werte, en]);

  useEffect(() => { if (meldung && meldungRef.current) meldungRef.current.focus(); }, [meldung]);
  useEffect(() => {
    if (ergebnis && karte.current) {
      window.scrollTo({ top: karte.current.getBoundingClientRect().top + window.scrollY - 110, behavior: 'smooth' });
    }
  }, [ergebnis]);

  const absenden = async e => {
    e.preventDefault();
    if (sendet) return;
    setVersucht(true);
    const f = pruefe(werte, en);
    setFehler(f);
    setMeldung(null);
    if (Object.keys(f).length) {
      const erstes = { name: 'vw-name', email: 'vw-email', vertrag: 'vw-angaben' }[Object.keys(f)[0]];
      const el = document.getElementById(erstes);
      if (el) el.focus();
      return;
    }
    setSendet(true);
    const abbruch = new AbortController();
    const uhr = setTimeout(() => abbruch.abort(), 20000);
    try {
      const res = await fetch(`${API_URL}/api/withdrawal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: abbruch.signal,
        body: JSON.stringify({
          name: zusammen(werte.name),
          email: werte.email.trim(),
          contract_type: werte.typ,
          contract: werte.angaben.trim(),
          order_date: werte.datum,
          lang: en ? 'en' : 'de',
          website: werte.website,
        }),
      });
      let daten = null;
      try { daten = await res.json(); } catch { daten = null; }
      if (res.ok && daten && daten.ok) {
        setErgebnis({ ...daten, email: werte.email.trim() });
      } else if (res.status === 429) {
        setMeldung('zuviel');
      } else if (res.status === 400 && daten && typeof daten.detail === 'string') {
        setMeldung({ text: daten.detail });
      } else if (res.status === 422) {
        setMeldung({ text: T('Bitte prüf deine Angaben (ein Feld ist zu lang oder leer).', 'Please check your details (a field is too long or empty).') });
      } else {
        setMeldung('server');
      }
    } catch {
      setMeldung('server');
    } finally {
      clearTimeout(uhr);
      setSendet(false);
    }
  };

  const fehlerText = id => fehler[id] && <p className="vw-feldfehler" id={`vw-${id}-fehler`}>{fehler[id]}</p>;

  return (
    <motion.div {...SEITEN_BLENDE}>
      <PageMeta
        title={T('Vertrag widerrufen | DAVIDMEDIA', 'Withdraw from contract | DAVIDMEDIA')}
        description={T('Widerrufe deinen Vertrag mit David Media online in zwei Schritten. Du bekommst sofort eine Eingangsbestätigung per E-Mail.',
          'Withdraw from your contract with David Media online in two steps. You get a confirmation of receipt by email right away.')}
        url={`https://www.davidmedia.de${en ? '/en/withdraw' : '/vertrag-widerrufen'}`}
        noindex
      />
      <FloatingHeader />
      <div className="rechtsseite vw-seite" lang={en ? 'en' : 'de'}>
        <Lichtszene />
        <header className="recht-kopf">
          <span className="kicker">{T('Widerruf', 'Withdrawal')}</span>
          <h1>{T('Vertrag widerrufen', 'Withdraw from contract')}<span className="grau">{T('Online, in zwei Schritten. Ohne Begründung.', 'Online, in two steps. No reason needed.')}</span></h1>
        </header>

        <div className="vw-raster">
          <div className={'vw-karte' + (ergebnis ? ' vw-druck' : '')} ref={karte}>
            {!ergebnis ? (
              <form onSubmit={absenden} noValidate>
                <ol className="vw-schritte" aria-label={T('Ablauf', 'Steps')}>
                  <li className={vollstaendig ? 'fertig' : 'an'}><b>01</b>{T('Angaben', 'Details')}</li>
                  <li className={vollstaendig ? 'an' : ''}><b>02</b>{T('Widerruf bestätigen', 'Confirm withdrawal')}</li>
                </ol>

                <fieldset className="vw-abschnitt">
                  {/* Kicker IN der legend: eine legend steht sonst immer ganz oben im fieldset */}
                  <legend><span className="kicker vw-nr">{T('Schritt 1', 'Step 1')}</span>{T('Deine Angaben', 'Your details')}</legend>
                  <p className="vw-unter">{T('Wir brauchen nur deinen Namen, den Vertrag und eine E-Mail-Adresse für die Bestätigung. Einen Grund musst du nicht angeben.',
                    'We only need your name, the contract and an email address for the confirmation. You don’t have to give a reason.')}</p>

                  <div className="vw-felder">
                    <div className="vw-feld">
                      <label htmlFor="vw-name">{T('Name', 'Name')}</label>
                      <input id="vw-name" name="name" type="text" autoComplete="name" maxLength={200}
                        value={werte.name} onChange={e => setze('name', e.target.value)}
                        placeholder={T('Vor- und Nachname', 'First and last name')}
                        aria-invalid={!!fehler.name} aria-describedby={fehler.name ? 'vw-name-fehler' : undefined} />
                      {fehlerText('name')}
                    </div>
                    <div className="vw-feld">
                      <label htmlFor="vw-email">{T('E-Mail für die Bestätigung', 'Email for the confirmation')}</label>
                      <input id="vw-email" name="email" type="email" autoComplete="email" inputMode="email" maxLength={200}
                        value={werte.email} onChange={e => setze('email', e.target.value)}
                        placeholder={T('name@beispiel.de', 'name@example.com')}
                        aria-invalid={!!fehler.email} aria-describedby={fehler.email ? 'vw-email-fehler' : 'vw-email-hilfe'} />
                      {fehler.email ? fehlerText('email') : <p className="vw-hilfe" id="vw-email-hilfe">{T('Am besten die Adresse, mit der du bestellt hast.', 'Ideally the address you ordered with.')}</p>}
                    </div>

                    <div className="vw-feld breit" role="radiogroup" aria-labelledby="vw-typ-titel">
                      <span className="vw-label" id="vw-typ-titel">{T('Welchen Vertrag möchtest du widerrufen?', 'Which contract do you want to withdraw from?')}</span>
                      <div className="vw-wahl" data-fehler={fehler.vertrag ? 'ja' : undefined}>
                        {[
                          ['kauf', T('Kauf eines Werkzeugs', 'Purchase of a tool'), T('Einmalkauf, z. B. Shake Flow oder Instant Grade', 'One-time purchase, e.g. Shake Flow or Instant Grade')],
                          ['abo', T('All-in-One-Abo', 'All-in-One subscription'), T('Studio Flow mit allen Werkzeugen, monatlich oder jährlich', 'Studio Flow with every tool, monthly or yearly')],
                        ].map(([wert, titel, klein]) => (
                          <label key={wert}>
                            <input type="radio" name="typ" value={wert} checked={werte.typ === wert}
                              onChange={() => setze('typ', wert)}
                              onClick={() => { if (werte.typ === wert) setze('typ', ''); }} />
                            <span className="vw-punkt" aria-hidden="true" />
                            <b>{titel}</b>
                            <span className="vw-klein">{klein}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="vw-feld breit">
                      <label htmlFor="vw-angaben">{T('Angaben zum Vertrag', 'Contract details')}<small className="vw-tag">{werte.typ ? T('Optional', 'Optional') : ''}</small></label>
                      <textarea id="vw-angaben" name="angaben" rows={3} maxLength={1000}
                        value={werte.angaben} onChange={e => setze('angaben', e.target.value)}
                        placeholder={T('z. B. Bestellnummer DM-B-0042, Lizenzschlüssel oder Produktname', 'e.g. order number DM-B-0042, license key or product name')}
                        aria-invalid={!!fehler.vertrag} aria-describedby={fehler.vertrag ? 'vw-vertrag-fehler' : 'vw-angaben-hilfe'} />
                      {fehler.vertrag ? fehlerText('vertrag') : (
                        <p className="vw-hilfe" id="vw-angaben-hilfe">{T('Bestellnummer oder Lizenzschlüssel helfen uns beim Zuordnen, sind aber kein Muss. Hast du mehrmals gekauft, schreib dazu, welchen Kauf du widerrufst.',
                          'An order number or license key helps us find it, but isn’t required. If you bought more than once, tell us which purchase you are withdrawing from.')}</p>
                      )}
                    </div>

                    <div className="vw-feld">
                      <label htmlFor="vw-datum">{T('Bestellt am', 'Ordered on')}<small className="vw-tag">{T('Optional', 'Optional')}</small></label>
                      <input id="vw-datum" name="datum" type="date" max={heute()}
                        value={werte.datum} onChange={e => setze('datum', e.target.value)} />
                    </div>
                  </div>

                  {/* Honigtopf: Menschen sehen und erreichen das Feld nicht, Bots füllen es aus */}
                  <div className="vw-hp" aria-hidden="true">
                    <label htmlFor="vw-website">Website</label>
                    <input id="vw-website" name="website" type="text" tabIndex={-1} autoComplete="off"
                      value={werte.website} onChange={e => setze('website', e.target.value)} />
                  </div>
                </fieldset>

                <section className="vw-abschnitt" aria-labelledby="vw-schritt2">
                  <span className="kicker vw-nr">{T('Schritt 2', 'Step 2')}</span>
                  <h2 id="vw-schritt2">{T('Widerruf bestätigen', 'Confirm withdrawal')}</h2>
                  <p className="vw-unter">{T('Das schickst du ab:', 'This is what you send:')}</p>
                  <div className={'vw-erklaerung' + (vollstaendig ? '' : ' leer')} aria-live="polite">
                    {vollstaendig ? (
                      <><span className="kicker">{T('Deine Erklärung', 'Your declaration')}</span>{text}</>
                    ) : T('Sobald Name, E-Mail und Vertrag angegeben sind, steht hier deine Erklärung.',
                      'As soon as your name, email and contract are filled in, your declaration appears here.')}
                  </div>
                  <div className="vw-absenden">
                    <button type="submit" className="btn primaer" disabled={sendet} data-testid="widerruf-bestaetigen">
                      {sendet && <span className="vw-dreher" aria-hidden="true" />}
                      {T('Widerruf bestätigen', 'Confirm withdrawal')}
                    </button>
                    <p>{T('Mit dem Klick schickst du den Widerruf ab. Die Eingangsbestätigung kommt sofort per E-Mail.',
                      'Clicking sends your withdrawal. The confirmation of receipt arrives by email right away.')}</p>
                  </div>

                  {meldung && (
                    <div className="vw-meldung" role="alert" tabIndex={-1} ref={meldungRef}>
                      {meldung === 'server' && (en ? (
                        <><b>The server isn’t responding right now.</b> Your withdrawal has <b>not</b> been sent yet. Please try again in a few minutes. You can also withdraw by email or letter, the details are in the <Link to={belehrung}>withdrawal policy</Link>.</>
                      ) : (
                        <><b>Der Server antwortet gerade nicht.</b> Dein Widerruf wurde <b>noch nicht</b> übermittelt. Bitte versuch es in ein paar Minuten noch einmal. Du kannst auch per E-Mail oder Brief widerrufen, die Angaben stehen in der <Link to={belehrung}>Widerrufsbelehrung</Link>.</>
                      ))}
                      {meldung === 'zuviel' && T('Zu viele Versuche in kurzer Zeit. Bitte warte ein paar Minuten und versuch es dann noch einmal.',
                        'Too many attempts in a short time. Please wait a few minutes and try again.')}
                      {typeof meldung === 'object' && meldung.text}
                    </div>
                  )}
                </section>
              </form>
            ) : (
              <div className="vw-fertig" role="status">
                <div className="vw-haken" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg></div>
                <h2>{T('Dein Widerruf ist eingegangen.', 'Your withdrawal has been received.')}</h2>
                <p className="vw-unter">
                  {ergebnis.confirmation_sent
                    ? T(`Die Eingangsbestätigung ist per E-Mail an ${ergebnis.email} unterwegs.`, `The confirmation of receipt is on its way to ${ergebnis.email}.`)
                    : T('Wir haben deinen Widerruf gespeichert.', 'We have saved your withdrawal.')}
                </p>
                <dl className="vw-daten">
                  <div><dt>{T('Eingegangen am', 'Received on')}</dt><dd>{ergebnis.received_at_text}</dd></div>
                  <div><dt>{T('Referenz', 'Reference')}</dt><dd className="mono">{ergebnis.reference}</dd></div>
                  <div><dt>{T('Bestätigung an', 'Confirmation to')}</dt><dd>{ergebnis.email}</dd></div>
                </dl>
                <div className="vw-erklaerung"><span className="kicker">{T('Deine Erklärung', 'Your declaration')}</span>{ergebnis.declaration}</div>
                {!ergebnis.confirmation_sent && (
                  <p className="vw-hinweis warnung">{T('Die Bestätigungs-Mail konnte gerade nicht verschickt werden. Dein Widerruf ist trotzdem eingegangen. Sichere diese Seite als Nachweis (Drucken › Als PDF sichern); wir schicken dir die Bestätigung nach.',
                    'The confirmation email could not be sent just now. Your withdrawal has still been received. Save this page as proof (Print › Save as PDF); we will send you the confirmation later.')}</p>
                )}
                <p className="vw-hinweis">{T('Die Bestätigung belegt den Eingang. Ob und in welchem Umfang der Widerruf wirksam ist, prüfen wir jetzt und melden uns bei dir.',
                  'The confirmation proves receipt. We will now check whether and to what extent the withdrawal is effective and get back to you.')}</p>
                <div className="vw-knoepfe">
                  <button type="button" className="btn glas" onClick={() => window.print()}>{T('Drucken oder als PDF sichern', 'Print or save as PDF')}</button>
                  <Link className="btn glas" to={en ? '/en' : '/'}>{T('Zur Startseite', 'Back to home')}</Link>
                </div>
              </div>
            )}
          </div>

          <aside className="vw-seitenleiste" aria-label={T('So läuft es', 'How it works')}>
            <div className="vw-info">
              <span className="kicker">{T('So läuft es', 'How it works')}</span>
              <ol>
                <li><b>{T('Angaben machen', 'Enter your details')}</b><span>{T('Name, Vertrag und E-Mail. Ein Grund ist nicht nötig.', 'Name, contract and email. No reason needed.')}</span></li>
                <li><b>{T('Widerruf bestätigen', 'Confirm withdrawal')}</b><span>{T('Erst dieser Klick schickt den Widerruf ab.', 'Only this click sends the withdrawal.')}</span></li>
                <li><b>{T('Eingangsbestätigung', 'Confirmation of receipt')}</b><span>{T('Kommt sofort per E-Mail, mit deiner Erklärung und Datum und Uhrzeit des Eingangs.', 'Arrives by email right away, with your declaration and the date and time of receipt.')}</span></li>
              </ol>
            </div>
            <div className="vw-info">
              <span className="kicker">{T('Empfänger', 'Recipient')}</span>
              <address>David Ferfers · David Media<br />Bruchhöfe 21, 47829 Krefeld{en ? ', Germany' : ''}</address>
            </div>
            <div className="vw-info">
              <span className="kicker">{T('Andere Wege', 'Other ways')}</span>
              <p>{T('Du kannst auch weiterhin per E-Mail oder Brief widerrufen. Alles zu Fristen und Folgen steht in der ', 'You can still withdraw by email or letter as well. Everything about deadlines and consequences is in the ')}
                <Link to={belehrung}>{T('Widerrufsbelehrung', 'withdrawal policy')}</Link>.</p>
              <p>{T('Fragen? ', 'Questions? ')}<Link to={`${p}/support`}>{T('Support & FAQ', 'Support & FAQ')}</Link></p>
            </div>
          </aside>
        </div>
      </div>
      <div className="relative z-10"><Footer /></div>
    </motion.div>
  );
}
